import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Award,
  AlertCircle,
  ClipboardList,
  RefreshCw,
  CheckCircle2,
} from 'lucide-react';
import { quizResultService } from '../../services/quizResultService';
import type { StudentQuizScore } from '../../types/quizResult';
import { cardTheme } from '../../styles/cardTheme';
import QuizScoreCard from '../../components/quiz/QuizScoreCard';

type FilterMode = 'all' | 'solved' | 'pending';

function isQuizSolved(quiz: StudentQuizScore): boolean {
  return quiz.submittedAt != null;
}

function getScorePercent(quiz: StudentQuizScore): number {
  if (!quiz.fullMark) return 0;
  return Math.round((quiz.score / quiz.fullMark) * 100);
}

const PortalGrades: React.FC = () => {
  const [quizzes, setQuizzes] = useState<StudentQuizScore[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterMode>('all');

  const loadQuizzes = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    const result = await quizResultService.getStudentQuizzes();
    if (result.success) {
      setQuizzes(result.data);
    } else {
      setLoadError(result.error);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadQuizzes();
  }, [loadQuizzes]);

  const solvedQuizzes = useMemo(() => quizzes.filter(isQuizSolved), [quizzes]);
  const pendingQuizzes = useMemo(() => quizzes.filter((q) => !isQuizSolved(q)), [quizzes]);

  const filteredQuizzes = useMemo(() => {
    if (filter === 'solved') return solvedQuizzes;
    if (filter === 'pending') return pendingQuizzes;
    return quizzes;
  }, [filter, quizzes, solvedQuizzes, pendingQuizzes]);

  const averagePercent = useMemo(() => {
    if (solvedQuizzes.length === 0) return 0;
    const total = solvedQuizzes.reduce((sum, q) => sum + getScorePercent(q), 0);
    return Math.round(total / solvedQuizzes.length);
  }, [solvedQuizzes]);

  const filterButtons: { id: FilterMode; label: string; count: number }[] = [
    { id: 'all', label: 'الكل', count: quizzes.length },
    { id: 'solved', label: 'محلولة', count: solvedQuizzes.length },
    { id: 'pending', label: 'لم تُحل', count: pendingQuizzes.length },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className={`text-2xl sm:text-3xl font-bold ${cardTheme.title}`}>درجاتي</h1>
            <p className="mt-1 text-sm sm:text-base text-gray-600">
              نتائج اختباراتك ودرجاتك في المنصة
            </p>
          </div>
          <button
            type="button"
            onClick={loadQuizzes}
            disabled={isLoading}
            className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-green-200 px-3 py-2 text-sm font-semibold text-green-800 hover:bg-green-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">تحديث</span>
          </button>
        </div>
      </div>

      {!isLoading && !loadError && quizzes.length > 0 && (
        <div className="mb-6 grid grid-cols-1 gap-3 min-[480px]:grid-cols-3">
          <div className={`${cardTheme.shell} p-4 sm:p-5`}>
            <div className="flex items-center gap-3">
              <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${cardTheme.iconBox}`}>
                <ClipboardList className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-gray-500">إجمالي الاختبارات</p>
                <p className="text-xl font-bold text-green-900">{quizzes.length}</p>
              </div>
            </div>
          </div>
          <div className={`${cardTheme.shell} p-4 sm:p-5`}>
            <div className="flex items-center gap-3">
              <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${cardTheme.iconBoxGold}`}>
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-gray-500">اختبارات محلولة</p>
                <p className="text-xl font-bold text-green-900">{solvedQuizzes.length}</p>
              </div>
            </div>
          </div>
          <div className={`${cardTheme.shell} p-4 sm:p-5`}>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-100 text-green-800">
                <Award className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-gray-500">متوسط الدرجات</p>
                <p className="text-xl font-bold text-green-900">
                  {solvedQuizzes.length > 0 ? `${averagePercent}%` : '—'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {quizzes.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {filterButtons.map((btn) => (
            <button
              key={btn.id}
              type="button"
              onClick={() => setFilter(btn.id)}
              className={`min-h-10 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                filter === btn.id
                  ? 'bg-green-700 text-white shadow-md'
                  : 'bg-white border border-green-200 text-green-800 hover:bg-green-50'
              }`}
            >
              {btn.label} ({btn.count})
            </button>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 min-[640px]:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={`${cardTheme.shell} h-52 animate-pulse bg-green-50/50`} />
          ))}
        </div>
      ) : loadError ? (
        <div className={`${cardTheme.shell} p-6 sm:p-8 text-center`}>
          <AlertCircle className="mx-auto mb-3 h-10 w-10 text-red-500" />
          <p className="text-sm sm:text-base text-red-700">{loadError}</p>
          <button
            type="button"
            onClick={loadQuizzes}
            className={`mt-4 min-h-11 rounded-xl px-5 text-sm font-bold ${cardTheme.btnPrimary}`}
          >
            إعادة المحاولة
          </button>
        </div>
      ) : filteredQuizzes.length === 0 ? (
        <div className={`${cardTheme.shell} p-8 sm:p-12 text-center`}>
          <Award className="mx-auto mb-4 h-12 w-12 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-900">لا توجد نتائج</h3>
          <p className="mt-2 text-sm text-gray-600">
            {filter === 'solved'
              ? 'لم تحل أي اختبار بعد. ابدأ بحل الاختبارات في حصصك.'
              : filter === 'pending'
              ? 'رائع! لا توجد اختبارات معلّقة حالياً.'
              : 'لا توجد اختبارات متاحة حالياً.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 min-[640px]:grid-cols-2">
          {filteredQuizzes.map((quiz) => (
            <QuizScoreCard key={quiz.quizId} quiz={quiz} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalGrades;
